/**
 * useSuperusers Hook
 * Provides access to superuser data with CRUD operations
 */

import { useAtom } from "jotai";
import { useMemo, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { superusersQueryAtom, usersQueryAtom } from "@/store/adminDataStore";
import { employeesQueryAtom } from "@/store/referenceDataStore";
import { adminAPI } from "@/services";
import type { ISuperuser, ISuperuserCreateRequest, IUser } from "@/services";
import {
  applyNameFallback,
  fallbackFromEmployee,
  fallbackFromUser,
  normalizeEmail,
  upsertNameFallback,
} from "./dataQuality";
import type { NameFallback } from "./dataQuality";
import { invalidateWithTimeout } from "@/utils/queryUtils";

export interface UseSuperusersReturn {
  /** All superusers from API */
  superusers: ISuperuser[];
  /** Superusers sorted alphabetically by name */
  sortedSuperusers: ISuperuser[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Find superuser by ID */
  findById: (id: number) => ISuperuser | undefined;
  /** Find superuser by user ID */
  findByUserId: (userId: number) => ISuperuser | undefined;
  /** Create a new superuser */
  createSuperuser: (
    data: ISuperuserCreateRequest,
  ) => Promise<ISuperuser | null>;
  /** Delete a superuser by email */
  deleteSuperuser: (email: string) => Promise<boolean>;
  /** Creating state */
  isCreating: boolean;
  /** Create error */
  createError: Error | null;
  /** Deleting state */
  isDeleting: boolean;
  /** Delete error */
  deleteError: Error | null;
}

export function useSuperusers(): UseSuperusersReturn {
  const [query] = useAtom(superusersQueryAtom);
  const [usersQuery] = useAtom(usersQueryAtom);
  const [employeesQuery] = useAtom(employeesQueryAtom);
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<Error | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<Error | null>(null);

  // Stable reference to prevent unnecessary recalculations
  const rawSuperusers = useMemo(() => query.data ?? [], [query.data]);
  const fallbackMap = useMemo(() => {
    const map = new Map<string, NameFallback>();
    employeesQuery.data?.forEach((employee) => {
      upsertNameFallback(map, employee.email, fallbackFromEmployee(employee));
    });
    usersQuery.data?.forEach((user) => {
      upsertNameFallback(map, user.email, fallbackFromUser(user));
    });
    return map;
  }, [employeesQuery.data, usersQuery.data]);

  const usersByEmail = useMemo(() => {
    const map = new Map<string, IUser>();
    usersQuery.data?.forEach((user) => {
      const normalized = normalizeEmail(user.email);
      if (normalized) {
        map.set(normalized, user);
      }
    });
    return map;
  }, [usersQuery.data]);

  const superusers = useMemo(() => {
    if (!rawSuperusers.length) {
      return [];
    }
    return rawSuperusers.flatMap((entry, index) => {
      let candidate: ISuperuser | null = null;

      if (typeof entry === "string") {
        const normalized = normalizeEmail(entry);
        if (!normalized) return [];
        const fallback = fallbackMap.get(normalized);
        const userMatch = usersByEmail.get(normalized);
        const baseUser: ISuperuser = {
          id: userMatch?.id ?? -(index + 1),
          user_id: userMatch?.id ?? -(index + 1),
          name: userMatch?.name ?? entry,
          email: entry,
          first_name: userMatch?.first_name,
          last_name: userMatch?.last_name,
        };
        candidate = applyNameFallback(baseUser, fallback);
      } else {
        const emailKey = normalizeEmail(entry.email);
        const fallback = emailKey ? fallbackMap.get(emailKey) : undefined;
        candidate = applyNameFallback(entry, fallback);
      }

      if (!candidate) {
        return [];
      }

      const normalizedEmail = normalizeEmail(candidate.email);
      if (!normalizedEmail) {
        return [];
      }
      return [candidate];
    });
  }, [rawSuperusers, fallbackMap, usersByEmail]);

  // Sort alphabetically by name
  const sortedSuperusers = useMemo(
    () =>
      [...superusers].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? ""),
      ),
    [superusers],
  );

  // Find by ID
  const findById = useCallback(
    (id: number) => superusers.find((s) => s.id === id),
    [superusers],
  );

  // Find by user ID
  const findByUserId = useCallback(
    (userId: number) => superusers.find((s) => s.user_id === userId),
    [superusers],
  );

  // Create superuser mutation
  const createSuperuser = useCallback(
    async (data: ISuperuserCreateRequest): Promise<ISuperuser | null> => {
      setIsCreating(true);
      setCreateError(null);

      try {
        const result = await adminAPI.createSuperuser(data);
        // Invalidate and refetch superusers
        await invalidateWithTimeout(queryClient, ["superusers"]);
        return result;
      } catch (error) {
        setCreateError(error as Error);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [queryClient],
  );

  // Delete superuser mutation
  const deleteSuperuser = useCallback(
    async (email: string): Promise<boolean> => {
      setIsDeleting(true);
      setDeleteError(null);

      try {
        await adminAPI.deleteSuperuser(email);
        // Invalidate and refetch superusers
        await invalidateWithTimeout(queryClient, ["superusers"]);
        return true;
      } catch (error) {
        setDeleteError(error as Error);
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [queryClient],
  );

  return {
    superusers,
    sortedSuperusers,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
    findById,
    findByUserId,
    createSuperuser,
    deleteSuperuser,
    isCreating,
    createError,
    isDeleting,
    deleteError,
  };
}
